// What the quick find in the header offers: the menu pages, the tools and the help articles.
// Every route of the app is either reached through one of these or listed in notInQuickFind,
// which a unit test checks, so a new page cannot be left out of the quick find by accident.

import routes from '@config/routes';
import { visibleNavigation } from '@config/navigation';
import helpArticles from '@config/helpArticles';
import tools, { toolGroupLabelKeys, toolLink } from '@config/tools';

export type QuickFindAccess = { isAuthenticated: boolean; isProjectMember: boolean };

export type QuickFindPlace = {
  kind: 'page' | 'help';
  path: string;
  /** i18n keys; contextKey is shown next to the title, e.g. the menu section */
  labelKey: string;
  contextKey?: string;
  descriptionKey: string;
  /** i18n key of other words it is found by, e.g. a former name */
  keywordsKey?: string;
  /** i18n key of a help article's text, searched but not shown */
  contentKey?: string;
};

/** Routes that are not offered in the quick find, and why. */
export const notInQuickFind: string[] = [
  routes.home, // the logo
  routes.login,
  routes.accessDenied,
  routes.forbidden,
  routes.admin, // not built yet
  routes.reports, // redirects to the status reports
  routes.map, // under development, not in the menu
  routes.helpMap, // under development
  routes.dataTreehouseValidation, // its tools card is not released yet
  routes.dereference, // reached from links to data and ontology URIs
];

/** The places the user can open, pages first. */
export const quickFindPlaces = ({ isAuthenticated, isProjectMember }: QuickFindAccess): QuickFindPlace[] => {
  const pages: QuickFindPlace[] = isAuthenticated
    ? visibleNavigation(isProjectMember).flatMap((section) =>
        section.pages.map((page) => ({
          kind: 'page' as const,
          path: page.path,
          labelKey: page.labelKey,
          contextKey: section.labelKey,
          descriptionKey: page.descriptionKey,
          keywordsKey: page.keywordsKey,
        })),
      )
    : [];
  const toolPages: QuickFindPlace[] = tools
    .filter((tool) => (tool.requiredAccessLevel === 'projectMember' ? isProjectMember : isAuthenticated))
    .map((tool) => ({
      kind: 'page' as const,
      path: toolLink(tool),
      labelKey: tool.nameKey,
      contextKey: toolGroupLabelKeys[tool.group],
      descriptionKey: tool.descriptionKey,
      keywordsKey: tool.keywordsKey,
    }));
  const help: QuickFindPlace[] = [
    { kind: 'help', path: routes.help, labelKey: 'help.title', descriptionKey: 'help.ingress' },
    ...helpArticles.map((article) => ({
      kind: 'help' as const,
      path: article.path,
      labelKey: `help.${article.id}`,
      descriptionKey: `help.descriptions.${article.id}`,
      contentKey: `help.content.${article.id}`,
    })),
  ];
  return [...pages, ...toolPages, ...help];
};
